import React, { useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import { connect } from 'scrivito'

import {
  FilterBuilder,
  useFilterBuilder,
  SavedFilterStructure,
} from '@/Atomic/Organisms/FilterBuilder'
import { Modal } from '@/Atomic/Organisms/Modal'
import { List } from '@/Data/List/ListDataClass'
import { useEntitySchema } from '@/hooks/useEntitySchema'
import './SegmentFilterModal.scss'

export interface SegmentFilterModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (filter: SavedFilterStructure) => void
  initialFilter?: SavedFilterStructure
  entityName?: string
  listId?: string
}

export const SegmentFilterModal: React.FC<SegmentFilterModalProps> = connect(
  ({
    isOpen,
    onClose,
    onSave,
    initialFilter,
    entityName = 'person',
    listId,
  }: SegmentFilterModalProps) => {
    const { t } = useTranslation()
    const { attributes: baseSchemaAttributes, relations: schemaRelations } =
      useEntitySchema(entityName)

    let listItem: ReturnType<typeof List.get> | undefined
    let listQuery: Record<string, unknown> | undefined

    if (listId) {
      try {
        listItem = List.get(listId)
        listQuery = listItem?.get('query') as
          | Record<string, unknown>
          | undefined
      } catch {
        // data not yet loaded by Scrivito
      }
    }

    const apiFilter = listQuery?.filters as SavedFilterStructure | undefined

    const {
      filter,
      setFilter,
      loadFromSavedFilter,
      validateFilter,
      toSavedFormat,
      createEmptyFilter,
      errors,
      clearError,
    } = useFilterBuilder({
      entityName,
      initialFilter,
      baseSchemaAttributes,
      schemaRelations,
    })

    // Load filter when modal opens
    useEffect(() => {
      if (!isOpen) return

      if (apiFilter && baseSchemaAttributes) {
        loadFromSavedFilter(apiFilter)
      } else if (!initialFilter) {
        setFilter(createEmptyFilter())
      }
    }, [
      isOpen,
      apiFilter,
      initialFilter,
      baseSchemaAttributes,
      loadFromSavedFilter,
      setFilter,
      createEmptyFilter,
    ])

    const handleSave = async () => {
      const { isValid } = validateFilter()

      if (!isValid) {
        return
      }

      const transformedFilter = toSavedFormat()

      if (listItem) {
        // Persist filters to the list's query.filters via API
        try {
          const payload = { ...listQuery, filters: transformedFilter }
          await listItem.update({
            query: payload,
          })
        } catch (e) {
          console.error('Failed to save filter to list:', e)
        }
      }

      onSave(transformedFilter)
      onClose()
    }

    return (
      <Modal isOpen={isOpen} onClose={onClose} className="segment-filter-modal">
        <Modal.Header title={t('filter.modal.title', 'Filter')} />
        <Modal.Body>
          <FilterBuilder
            filter={filter}
            onChange={setFilter}
            entityName={entityName}
            errors={errors}
            onClearError={clearError}
          />
        </Modal.Body>
        <Modal.Footer
          onConfirm={handleSave}
          onCancel={onClose}
          onConfirmText={t('common.save', 'Save')}
        />
      </Modal>
    )
  },
)
